import { Card, CardContent, Divider, Typography, IconButton, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useDataProvider } from "react-admin";
import React, { useEffect, useState } from "react";
import CircularLoader from "../loading/circularLoader";
import GenericError from "../error/genericError";
import Pagination from "../pagination/pagination";
import ModalConsumer from "../consumer/modalConsumer";
import ActionButton from "../button/actionButton";
import ViewWhitelistBankAccountsDrawer from "../drawer/viewWhitelistBankAccounts";
// Removed currency chip in favor of explicit bank account labels
import BankIcon from "../icons/bankIcon";
import copyIcon from "../images/copy-button.png";

// Utility function to get blockchain explorer URL for an address
const getBlockchainExplorerUrl = (address, networkName) => {
    if (!address || !networkName) return null;
    
    const lowerName = networkName.toLowerCase();
    
    // EVM-compatible chains
    if (lowerName.includes('evm') || lowerName === 'polygon') {
        return `https://polygonscan.com/address/${address}`;
    }
    if (lowerName === 'ethereum') {
        return `https://etherscan.io/address/${address}`;
    }
    if (lowerName.includes('berachain')) {
        return `https://berascan.com/address/${address}`;
    }
    if (lowerName.includes('hyperevm')) {
        return `https://explorer.hyperevm.com/address/${address}`;
    }
    if (lowerName.includes('ink')) {
        return `https://explorer.inkonchain.com/address/${address}`;
    }
    if (lowerName.includes('mantle')) {
        return `https://explorer.mantle.xyz/address/${address}`;
    }
    if (lowerName.includes('ronin')) {
        return `https://app.roninchain.com/address/${address}`;
    }
    if (lowerName.includes('sei')) {
        return `https://seitrace.com/address/${address}`;
    }
    
    // Non-EVM chains
    if (lowerName.includes('near')) {
        return `https://explorer.near.org/accounts/${address}`;
    }
    if (lowerName.includes('noble')) {
        return `https://www.mintscan.io/noble/address/${address}`;
    }
    if (lowerName.includes('solana')) {
        return `https://solscan.io/account/${address}`;
    }
    
    // Default for unknown chains - return null to not make it clickable
    return null;
};

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.surface.primary,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    content: {
        padding: '16px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '28px',
        gap: 12,
    },
    headerActions: {
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
    },
    pillGroup: {
        display: 'flex',
        gap: 8,
    },
    pill: {
        height: 40,
        width: 114,
        padding: 0,
        borderRadius: 54,
        border: '1px solid rgba(214,214,223,0.24)',
        color: theme.text.primary,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 1,
    },
    pillActive: {
        backgroundColor: '#A3A2FF',
        color: '#141417',
        border: 'none',
    },
    pillLabel: {
        width: 46,
        height: 26,
        marginLeft: 34,
        marginRight: 34,
        marginTop: 7,
        marginBottom: 7,
        fontFamily: 'DM Sans',
        fontWeight: 500,
        fontStyle: 'normal',
        fontSize: 14,
        lineHeight: '26px',
        letterSpacing: 0,
        textAlign: 'center',
        opacity: 1,
        display: 'inline-block',
    },
    pillLabelActive: {
        fontWeight: 700,
    },
    listRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 16,
        padding: '16px 8px',
        marginBottom: 12,
    },
    icon: {
        borderRadius: 8,
        width: 32,
        height: 32,
    },
    name: {
        color: '#FDFDFD',
        fontFamily: 'DM Sans',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '100%',
        letterSpacing: 0,
        height: 18,
        opacity: 1,
    },
    nameLinked: {
        color: '#FDFDFD',
        fontFamily: 'DM Sans',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '100%',
        letterSpacing: 0,
        height: 18,
        opacity: 1,
    },
    addressCrypto: {
        color: '#FDFDFD',
        fontFamily: 'SF Pro Text, -apple-system, system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: 15,
        lineHeight: '20px',
        letterSpacing: '-0.23px',
        height: 20,
        opacity: 0.5,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
    },
    addressCryptoClickable: {
        color: '#FDFDFD',
        fontFamily: 'SF Pro Text, -apple-system, system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: 15,
        lineHeight: '20px',
        letterSpacing: '-0.23px',
        height: 20,
        opacity: 0.5,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        cursor: 'pointer',
        textDecoration: 'none',
        '&:hover': {
            opacity: 0.8,
            textDecoration: 'underline',
        },
    },
    addressFiat: {
        color: '#FDFDFD',
        fontFamily: 'SF Pro Text, -apple-system, system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: 15,
        lineHeight: '20px',
        letterSpacing: '-0.23px',
        height: 20,
        opacity: 0.5,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    divider: {
        opacity: 0.23,
        backgroundColor: '#D6D6DF',
        marginLeft: 56,
        marginTop: 8,
        marginBottom: 8,
    },
    rowRight: {
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    rightInfo: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 4,
    },
    depositLabel: {
        color: '#FFFFFF',
        fontFamily: 'SF Pro Text, -apple-system, system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: 15,
        lineHeight: '20px',
        letterSpacing: '-0.23px',
        whiteSpace: 'nowrap'
    },
    depositFrom: {
        color: '#FDFDFD',
        fontFamily: 'SF Pro Text, -apple-system, system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: 15,
        lineHeight: '20px',
        letterSpacing: '-0.23px',
        opacity: 0.5,
        whiteSpace: 'nowrap'
    },
    depositValue: {
        color: '#FDFDFD',
        fontFamily: 'SF Pro Text, -apple-system, system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: 15,
        lineHeight: '20px',
        letterSpacing: '-0.23px',
        opacity: 0.5,
        whiteSpace: 'nowrap'
    },
    copyBtn: {
        marginLeft: 'auto',
        color: '#fff',
        opacity: 0.8,
        width: 17,
        height: 18,
        minWidth: 17,
        minHeight: 18,
        padding: 0,
        marginTop: 11,
        borderRadius: 0,
        '& .MuiTouchRipple-root': { display: 'none' },
        '&:hover': { opacity: 1 }
    },
    copyBtnInline: {
        marginLeft: 24,
        color: '#fff',
        opacity: 0.8,
        width: 17,
        height: 18,
        minWidth: 17,
        minHeight: 18,
        padding: 0,
        marginTop: 11,
        borderRadius: 0,
        '& .MuiTouchRipple-root': { display: 'none' },
        '&:hover': { opacity: 1 }
    },
    copyGlyph: {
        fontFamily: 'SF Pro Text, SF Pro Display, -apple-system, system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
        fontWeight: 400,
        fontStyle: 'normal',
        fontSize: 16,
        lineHeight: '18px',
        color: 'inherit',
        width: 17,
        height: 18,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    loaderWrap: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 200,
    },
    paginationContainer: {
        marginTop: 'auto',
        display: 'flex',
        justifyContent: 'flex-end',
        paddingRight: 16,
        paddingBottom: 8,
    }
}));

const PaymentMethods = ({ customerId, initialMode = 'crypto', useLinkedAccounts = false, showPills = true }) => {
    const classes = useStyles();
    const dataProvider = useDataProvider();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [wallets, setWallets] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [whitelistedAccounts, setWhitelistedAccounts] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [visitedPages, setVisitedPages] = useState([]);
    const perPage = 5;
    const [mode, setMode] = useState(initialMode); // 'crypto' | 'fiat'
    const [linkedAccounts, setLinkedAccounts] = useState([]);
    const [refreshVersion, setRefreshVersion] = useState(0);
    const [virtualAccounts, setVirtualAccounts] = useState([]);
    const [payId, setPayId] = useState(null);
    const [evmWallet, setEvmWallet] = useState(null);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        dataProvider.getOne('paymentMethods', { customerId })
            .then(({ data }) => {
                if (!mounted) return;
                const accounts = data?.result?.["accounts_Admin"] || [];
                const paymentsRoot = accounts?.[0]?.["paymentMethods"];
                const crypto = paymentsRoot?.crypto;
                const nonEvmWallets = crypto?.wallets?.filter(w => w?.network?.uriPrefix !== 'ethereum:') || [];
                setWallets(nonEvmWallets);
                // capture EVM wallet (stable address)
                const evm = crypto?.wallets?.find(w => w?.network?.uriPrefix === 'ethereum:') || null;
                setEvmWallet(evm);
                const whitelisted = paymentsRoot?.autoMatcherAccount?.whitelistedSourceAccounts || [];
                setWhitelistedAccounts(whitelisted);
                // capture PayID
                setPayId(paymentsRoot?.payId || null);
                // Also surface base Automatcher account (not just whitelisted)
                const baseAutomatcher = paymentsRoot?.autoMatcherAccount ? [{
                    displayAccountName: paymentsRoot.autoMatcherAccount.accountName,
                    accountName: paymentsRoot.autoMatcherAccount.accountName,
                    accountNumber: paymentsRoot.autoMatcherAccount.accountNumber,
                    bsbNumber: paymentsRoot.autoMatcherAccount.bsb,
                    isAutomatcher: true,
                }] : [];
                const whitelistedWithFlag = whitelisted.map(acc => ({...acc, isAutomatcher: false}));
                // Deduplicate base and whitelisted by bsbNumber + accountNumber
                const seenAuto = new Set();
                const combined = [];
                [...baseAutomatcher, ...whitelistedWithFlag].forEach(acc => {
                    const key = `${acc.bsbNumber}|${acc.accountNumber}`;
                    if (!seenAuto.has(key)) {
                        seenAuto.add(key);
                        combined.push(acc);
                    }
                });
                setBankAccounts(combined);
                const payableAccounts = data?.result?.["paymentMethods"]?.payables?.accountFiatMethods || [];
                setLinkedAccounts(payableAccounts);
                const va = accounts.flatMap(acc => acc?.paymentMethods?.virtualAccounts || []);
                setVirtualAccounts(va);
                setError(null);
            })
            .catch(setError)
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false }
    }, [customerId, refreshVersion]);

    useEffect(() => {
        setMode(initialMode);
    }, [initialMode]);

    if (loading) {
        return (
            <div className={classes.loaderWrap}>
                <CircularLoader trigger={loading} loaderSize='72px' />
            </div>
        );
    }

    if (error) {
        return <GenericError />
    }

    const items = mode === 'crypto'
        ? [
            ...(evmWallet?.address ? [{
                iconUrl: evmWallet?.network?.iconUrl ? evmWallet.network.iconUrl.replace(/[^/]+$/, 'polygon.png') : undefined,
                primary: 'EVM address',
                secondary: evmWallet.address,
                explorerUrl: getBlockchainExplorerUrl(evmWallet.address, 'EVM'),
            }] : []),
            ...wallets.map(({ address, network: { iconUrl, longName, name } }) => ({
                iconUrl,
                primary: longName || name,
                secondary: address,
                explorerUrl: getBlockchainExplorerUrl(address, longName || name),
            }))
        ]
        : (
            useLinkedAccounts
                ? linkedAccounts.map(({ accountName, accountDetailsText }) => ({
                    iconUrl: undefined,
                    primary: accountName,
                    secondary: accountDetailsText,
                }))
                : (() => {
                    // Show virtual accounts first if present
                    const vaItemsRaw = virtualAccounts.flatMap(va => (
                        (va.paymentMethods || []).map(pm => ({
                            iconUrl: undefined,
                            primary: `${va.currency} bank account`,
                            secondary: pm.accountDetailsDisplay,
                            currency: va.currency,
                            copy: pm.accountDetailsCopy
                        }))
                    ));
                    // De-duplicate identical virtual account rows (e.g., ACH/Wire with same details)
                    const seen = new Set();
                    const vaItems = [];
                    vaItemsRaw.forEach(item => {
                        const key = `${item.primary}|${item.secondary}`;
                        if (!seen.has(key)) {
                            seen.add(key);
                            vaItems.push(item);
                        }
                    });
            // Build a single Automatcher AUD row and merge whitelisted 'deposit from' into the right side
            const automatcherAcc = bankAccounts.find(acc => acc.isAutomatcher);
            const depositFromAcc = whitelistedAccounts && whitelistedAccounts.length > 0 ? whitelistedAccounts[0] : undefined;
            const automatcherItem = automatcherAcc ? {
                iconUrl: undefined,
                primary: 'AUD bank account',
                secondary: `BSB: ${automatcherAcc.bsbNumber} • Account: ${automatcherAcc.accountNumber}`,
                copy: `BSB: ${automatcherAcc.bsbNumber}\nAccount number: ${automatcherAcc.accountNumber}`,
                isAutomatcher: true,
                rightLabel: 'Deposit from',
                rightValue: depositFromAcc ? `${depositFromAcc.bsbNumber} • ${depositFromAcc.accountNumber}` : '',
                showUpdate: true
            } : null;
            // Append PayID and Stable (EVM) address
            const additionalFiatItems = [];
            if (payId?.payId) {
                additionalFiatItems.push({
                    iconUrl: undefined,
                    primary: 'PayID',
                    secondary: payId.payId,
                    copy: payId.payId
                });
            }
            return [...vaItems, ...(automatcherItem ? [automatcherItem] : []), ...additionalFiatItems]
                })()
        );

    const start = currentPage * perPage;
    const pageItems = items.slice(start, start + perPage);

    return (
        <Card elevation={0} className={classes.root}>
            <CardContent className={classes.content}>
                {(showPills || (useLinkedAccounts && mode === 'fiat')) && (
                    <div className={classes.headerRow}>
                        {showPills && (
                            <div className={classes.pillGroup}>
                                <div
                                    className={`${classes.pill} ${mode === 'crypto' ? classes.pillActive : ''}`}
                                    onClick={() => { setMode('crypto'); setCurrentPage(0); }}
                                >
                                    <span className={`${classes.pillLabel} ${mode === 'crypto' ? classes.pillLabelActive : ''}`}>Crypto</span>
                                </div>
                                <div
                                    className={`${classes.pill} ${mode === 'fiat' ? classes.pillActive : ''}`}
                                    onClick={() => { setMode('fiat'); setCurrentPage(0); }}
                                >
                                    <span className={`${classes.pillLabel} ${mode === 'fiat' ? classes.pillLabelActive : ''}`}>Fiat</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {pageItems.map((item, index) => (
                    <React.Fragment key={`${item.primary}|${item.secondary}`}>
                        <div className={classes.listRow}>
                            {item.iconUrl ? (
                                <img src={item.iconUrl} alt="" className={classes.icon} />
                            ) : (
                                <BankIcon />
                            )}
                            <div className={classes.column}>
                                <Typography className={useLinkedAccounts && mode === 'fiat' ? classes.nameLinked : classes.name}>{item.primary}</Typography>
                                {mode === 'crypto' && item.explorerUrl ? (
                                    <Typography
                                        component="a"
                                        href={item.explorerUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={classes.addressCryptoClickable}
                                    >
                                        {item.secondary}
                                    </Typography>
                                ) : (
                                    <Typography className={mode === 'crypto' ? classes.addressCrypto : classes.addressFiat}>{item.secondary}</Typography>
                                )}
                            </div>
                            {/* Right-side section for Automatcher AUD row */}
                            {item.isAutomatcher && (
                                <>
                                    {/* Copy sits right after automatcher details */}
                                    <Tooltip title={"Copy details"}>
                                        <IconButton aria-label="Copy" className={classes.copyBtnInline} onClick={() => navigator.clipboard.writeText(item.copy)}>
                                            <img
                                                src={copyIcon}
                                                alt="Copy"
                                                className={classes.copyGlyph}
                                                style={{transform: 'scale(1)', transformOrigin: 'center'}}
                                                draggable="false"
                                            />
                                        </IconButton>
                                    </Tooltip>
                                    <ModalConsumer>
                                        {({ showModal }) => (
                                            <div className={classes.rowRight}>
                                                <div className={classes.rightInfo}>
                                                    <Typography className={classes.depositLabel}>{item.rightLabel}</Typography>
                                                    {item.rightValue && (
                                                        <Typography className={classes.addressFiat}>{item.rightValue}</Typography>
                                                    )}
                                                </div>
                                                <ActionButton
                                                    size="sm"
                                                    variant="text"
                                                    onClick={() => showModal(ViewWhitelistBankAccountsDrawer, {
                                                        whitelistedSourceAccounts: whitelistedAccounts,
                                                        customerId,
                                                        onClose: () => {},
                                                        onUpdateComplete: () => setRefreshVersion(v => v + 1)
                                                    })}
                                                >
                                                    Update
                                                </ActionButton>
                                            </div>
                                        )}
                                    </ModalConsumer>
                                </>
                            )}
                            {!item.isAutomatcher && !(useLinkedAccounts && mode === 'fiat') && (
                                <Tooltip title={mode === 'crypto' ? "Copy address" : "Copy details"}>
                                    <IconButton aria-label="Copy" className={classes.copyBtn} onClick={() => navigator.clipboard.writeText(pageItems[index]?.copy || item.secondary)}>
                                        <img
                                            src={copyIcon}
                                            alt="Copy"
                                            className={classes.copyGlyph}
                                            style={{transform: 'scale(1)', transformOrigin: 'center'}}
                                            draggable="false"
                                        />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>
                        {index !== pageItems.length - 1 && (
                            <Divider className={classes.divider} />
                        )}
                    </React.Fragment>
                ))}
                {items.length === 0 && (
                    <Typography variant="body1">No items found.</Typography>
                )}
                {items.length > perPage && (
                    <div className={classes.paginationContainer}>
                        <Pagination
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            perPage={perPage}
                            total={items.length}
                            visitedPages={visitedPages}
                            setVisitedPages={setVisitedPages}
                        />
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default PaymentMethods;



